// FlightToast.jsx - Flight completion toast notifications
// Shows bottom-right notification when flight lands: route, revenue, pax count
// 3 seconds, auto-dismiss, click to dismiss early, stack up to 3

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
// Icons removed - text-only UI

// Toast Context
const FlightToastContext = createContext(null);

export function useFlightToast() {
  const context = useContext(FlightToastContext);
  if (!context) {
    throw new Error("useFlightToast must be used within FlightToastProvider");
  }
  return context;
}

// Format currency
const formatMoney = (amount) => {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${Math.round(amount)}`;
};

// Individual Toast Component
function FlightToastItem({ toast, onDismiss }) {
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 200);
  }, [toast.id, onDismiss]);

  // Auto-dismiss after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, 3000);
    return () => clearTimeout(timer);
  }, [handleDismiss]);

  const isDelayed = (toast.delayMinutes || 0) > 15;

  return (
    <div
      onClick={handleDismiss}
      className={`
        cursor-pointer transition-all duration-200
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
      `}
      style={{
        backgroundColor: "#0c0c0c",
        border: `1px solid ${isDelayed ? "#f59e0b" : "#22c55e"}`,
        borderRadius: "8px",
        padding: "12px 16px",
        minWidth: "280px",
        maxWidth: "340px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold" style={{ color: "#fff" }}>
          FLIGHT COMPLETE
        </span>
        <span style={{ color: "#525252" }}>X</span>
      </div>

      {/* Route */}
      <div className="text-lg font-bold mb-2" style={{ color: "#fff" }}>
        {toast.origin} - {toast.destination}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <span style={{ color: "#22c55e", fontWeight: "bold" }}>
          {formatMoney(toast.revenue || 0)}
        </span>
        <span style={{ color: "#a1a1a1" }}>
          {toast.passengers || 0} pax
        </span>
        {isDelayed ? (
          <span style={{ color: "#f59e0b" }}>
            ! {toast.delayMinutes}min late
          </span>
        ) : (
          <span style={{ color: "#22c55e" }}>On-time</span>
        )}
      </div>
    </div>
  );
}

// Toast Container
function FlightToastContainer({ toasts, onDismiss }) {
  // Only show last 3 toasts
  const visibleToasts = toasts.slice(-3);

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      style={{ pointerEvents: "auto" }}
    >
      {visibleToasts.map((toast) => (
        <FlightToastItem
          key={toast.id}
          toast={toast}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}

// Provider Component
export function FlightToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const showFlightComplete = useCallback((flightData) => {
    const id = `flight-toast-${++toastIdRef.current}`;
    const newToast = {
      id,
      origin: flightData.origin,
      destination: flightData.destination,
      revenue: flightData.revenue || flightData.estimatedRevenue || 0,
      passengers: flightData.passengers || flightData.passengerCount || 0,
      delayMinutes: flightData.totalDelayMinutes || flightData.delayMinutes || 0,
      timestamp: Date.now(),
    };

    setToasts((prev) => [...prev, newToast]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = {
    showFlightComplete,
    dismissToast,
  };

  return (
    <FlightToastContext.Provider value={value}>
      {children}
      <FlightToastContainer toasts={toasts} onDismiss={dismissToast} />
    </FlightToastContext.Provider>
  );
}

export default FlightToastProvider;
